import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertService } from '../../services/alert.service';
import { useForm } from 'react-hook-form';
import { PNNService } from '../../services/pnn.service';
import Loader from '../../components/loader/loader.component';
import Header from '../../components/layout/header.component';
import Asaid from '../../components/layout/asaid.component';
import Footer from '../../components/layout/footer.component';
import useBgPages from '../../hooks/use-bg-pages.hook';
import useRequest from '../../hooks/use-request.hook';
import Icon from '../../components/forms/icon.component';
import PrincipalCard from '../../components/cards/principal-card.component';
import Button from '../../components/forms/button.component';
import Input from '../../components/forms/input.component';
import Tooltip from '../../components/forms/tooltip.component';
import LogData from '../../components/logs/log-data.component';

function PNNPortabilidadPage() {
  useBgPages();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const [totalPNN, setTotalPNN] = useState(0);
  const [totalPortabilidad, setTotalPortabilidad] = useState(0);
  const [searchResult, setSearchResult] = useState<any>(null);
  const [numberSearch, setNumberSearch] = useState('');

  const defaultValues = {
    phone_number: ''
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const dataStatistics = [
    {
      label: t('pnn.statistics.pnn-records'),
      total: totalPNN,
      type: 'primary',
      icon: 'ki-phone'
    },
    {
      label: t('pnn.statistics.portability-records'),
      total: totalPortabilidad,
      type: 'primary',
      icon: 'ki-check-circle'
    }
  ];

  const InfoItem = ({ icon, title, children, type = 'primary' }: any) => (
    <div className="mb-4">
      <h4 className={`fw-bolder mb-2 d-flex align-items-center`}>
        <Icon icon={icon} size="fs-2x" className="me-1" type={type} />
        {title}
      </h4>
      {children}
    </div>
  );

  const EmptySection = ({ title, message }: any) => (
    <div className="mt-5 mb-10">
      <h3 className="text-info fw-bolder">{title}</h3>
      <h6 className="mt-4 d-flex align-items-center">
        <Icon icon="ki-information" size="fs-3x" className="me-2" />
        {message}
      </h6>
    </div>
  );

  const PNNSection = ({ pnnData, t }: any) => {
    const renderPNN = (label: string, value: React.ReactNode, isBadge = false, isLast = false) => (
      <p className={`mb-${isLast ? '0' : '2'}`}>
        <strong className="fs-6 text-primary fw-bolder">{label}:</strong>
        <br />
        {isBadge ? (
          <span className="badge badge-light-primary fs-7 fw-bolder">{value}</span>
        ) : (
          <span className="fs-6 text-dark fw-bolder">{value}</span>
        )}
      </p>
    );

    return (
      <div className="card bg-light mb-15">
        <div className="card-header">
          <h3 className="py-8 fw-bolder">{t('pnn.pnn-section.title')}</h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-10">
              <InfoItem icon="ki-phone" title={t('pnn.pnn-section.basic-info')}>
                <div className="separator separator-dashed border-primary p-1 mw-200px mb-3"></div>
                {renderPNN(
                  t('pnn.pnn-section.number'),
                  <span className="fs-5 fw-bold text-dark">{numberSearch}</span>
                )}
                {renderPNN(t('pnn.pnn-section.network-type'), pnnData.TIPO_RED, true, true)}
              </InfoItem>
            </div>

            <div className="col-md-4 mb-10">
              <InfoItem icon="ki-satellite" title={t('pnn.pnn-section.operator')}>
                <div className="separator separator-dashed border-primary p-1 mw-200px mb-3"></div>
                {renderPNN(t('pnn.pnn-section.short-name'), pnnData.NOMBRE_CORTO)}
                {renderPNN(t('pnn.pnn-section.company-name'), pnnData.RAZON_SOCIAL)}
                {renderPNN(t('pnn.pnn-section.zone'), pnnData.ZONA, false, true)}
              </InfoItem>
            </div>

            <div className="col-md-4 mb-10">
              <InfoItem icon="ki-code" title={t('pnn.pnn-section.technical-info')}>
                <div className="separator separator-dashed border-primary p-1 mw-200px mb-3"></div>
                {renderPNN(t('pnn.pnn-section.date-asignated'), pnnData.FECHA_ASIGNACION)}
                {renderPNN(t('pnn.pnn-section.modality'), pnnData.MODALIDAD)}
                {renderPNN(t('pnn.pnn-section.ocupation'), pnnData.OCUPACION)}
                {renderPNN(t('pnn.pnn-section.initial-numbering'), pnnData.NUMERACION_INICIAL)}
                {renderPNN(
                  t('pnn.pnn-section.final-numbering'),
                  pnnData.NUMERACION_FINAL,
                  false,
                  true
                )}
              </InfoItem>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PortabilitySection = ({ data, t }: any) => {
    const renderPortability = (
      value: React.ReactNode,
      label?: string,
      options: { isBadge?: boolean; isIcon?: boolean; isLast?: boolean } = {}
    ) => {
      const { isBadge = false, isLast = false } = options;

      return (
        <p className={`mb-${isLast ? '0' : '2'}`}>
          {label && (
            <>
              <strong className="fs-6 text-info fw-bolder">{label}:</strong>
              <br />
            </>
          )}
          {isBadge ? (
            <span className="badge badge-light-info fs-6 fw-bolder">{value}</span>
          ) : (
            <span className="fs-6 text-dark fw-bolder">{value}</span>
          )}
        </p>
      );
    };

    return (
      <div className="card bg-light mt-8">
        <div className="card-header">
          <h3 className="py-8 fw-bolder">{t('pnn.portability-section.title')}</h3>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-4 mb-10">
              <InfoItem
                icon="ki-phone"
                title={t('pnn.portability-section.ported-number')}
                type="info"
              >
                <div className="separator separator-dashed border-info text-info p-1 mw-200px mb-3"></div>
                {renderPortability(
                  <span className="fs-4 fw-bold text-dark">{numberSearch}</span>,
                  undefined,
                  { isLast: true }
                )}
              </InfoItem>
            </div>
            <div className="col-md-4 mb-10">
              <InfoItem
                icon="ki-arrow-right-left"
                title={t('pnn.portability-section.portability-type')}
                type="info"
              >
                <div className="separator separator-dashed border-info text-info p-1 mw-200px mb-3"></div>
                {renderPortability(
                  <>
                    <Icon
                      icon={data.mode === 'Entrada' ? 'ki-arrow-down' : 'ki-arrow-up'}
                      size="fs-3"
                      className="me-1"
                      type="info"
                    />
                    {data.mode}
                  </>,
                  undefined,
                  { isBadge: true, isLast: true }
                )}
              </InfoItem>
            </div>
            <div className="col-md-4 mb-10">
              <InfoItem
                icon="ki-satellite"
                title={t('pnn.portability-section.operators')}
                type="info"
              >
                <div className="separator separator-dashed border-info text-info p-1 mw-200px mb-3"></div>
                {renderPortability(data.operador, t('pnn.portability-section.flow'))}
                {renderPortability(
                  `RCR: ${data.rcr} | DCR: ${data.dcr}`,
                  t('pnn.portability-section.codes'),
                  { isIcon: true, isLast: true }
                )}
              </InfoItem>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const resetInfo = () => {
    setValue('phone_number', '');
    setSearchResult(null);
    setNumberSearch('');
  };

  const actionContent = (
    <div className="d-flex align-items-end">
      <form
        onSubmit={handleSubmit((data) => submit(data, 'searchPNN-83838377222'))}
        className="d-flex flex-wrap gap-3 align-items-end"
      >
        <div className="d-flex flex-column">
          <label className="form-label ms-1 d-flex align-items-center gap-2">
            {t('pnn.search-label')}{' '}
            <Tooltip title={t('pnn.search-tooltip')}>
              <Icon icon="ki-information-3" size="fs-4" type="info" />
            </Tooltip>
          </label>
          <Input
            requiredIndicator="required"
            placeholder={'5522334455'}
            errors={errors?.phone_number}
            {...register('phone_number', {
              required: {
                value: true,
                message: 'Ingresa un número de 10 dígitos'
              },
              setValueAs: (value) => value.trim(),
              pattern: {
                value: /^\d{10}$/,
                message: 'Ingresa un número de 10 dígitos'
              }
            })}
          />
        </div>
        <div
          className={`${Object.keys(errors).length > 0 ? 'mb-9' : 'mb-2'} ${searchResult ? '' : ''}`}
        >
          <Button
            type="submit"
            className="btn btn-primary fw-bolder"
            disabled={!isValid}
            isLoading={isSubmitting}
          >
            {isRequesting ? (
              <span className="spinner-border spinner-border-sm me-2"></span>
            ) : (
              <Icon icon="ki-magnifier" size="fs-3" className="me-2" />
            )}
            {t('pnn.search-button')}
          </Button>
        </div>
        {searchResult && (
          <div className={`${Object.keys(errors).length > 0 ? 'mb-9' : 'mb-2'}`}>
            <Button type="button" className="btn btn-info fw-bolder" onClick={resetInfo}>
              <Icon icon="ki-eraser" size="fs-3" className="me-2" />
              {t('general.clean')}
            </Button>
          </div>
        )}
      </form>
    </div>
  );

  const getData = async () => {
    try {
      const data = await call(() => PNNService.getStadisticsPNN());
      setTotalPNN(data.pnn);
      setTotalPortabilidad(data.portabilidad);
    } catch (error: any) {
      console.log('Error:', error);
      AlertService.toastError(error.error || error);
    }
  };

  const submit = async (data: any, buttonId: string) => {
    try {
      const pnnResult = await call(() => PNNService.getPNNforNumber(data.phone_number));
      setSearchResult(pnnResult);
      AlertService.toastSuccess(t('pnn.search-success'));
      setNumberSearch(data.phone_number);
      LogData(data, buttonId, window.location.href);
    } catch (error: any) {
      console.log('Error:', error);
      AlertService.toastError(error.error || error);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  onClick={() => {}}
                  title={t('pnn.page-title').split(' ').slice(0, -3).join(' ')}
                  titleBolder={t('pnn.page-title').split(' ').slice(-3).join(' ')}
                  subtitle={t('pnn.page-subtitle')}
                  breadcrumbItems={[t('pnn.breadcrumb.operations')]}
                  customActionContent={actionContent}
                  hideDefaultActions={true}
                />
                <div className="row g-xl-12 mb-5">
                  <div className="col-xl-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="mt-2">
                          <h3 className="fw-bolder mb-5 d-flex align-items-center mb-15">
                            {t('pnn.search-results-title')}
                            <span className="badge badge-light-info fs-4 ms-3">{numberSearch}</span>
                          </h3>
                          {!isRequesting ? (
                            searchResult ? (
                              <>
                                {searchResult.pnn ? (
                                  <PNNSection pnnData={searchResult.pnn} t={t} />
                                ) : (
                                  <EmptySection
                                    title="PNN"
                                    message={t('pnn.pnn-section.no-records')}
                                  />
                                )}

                                {searchResult.portabilidad ? (
                                  <PortabilitySection data={searchResult.portabilidad} t={t} />
                                ) : (
                                  <EmptySection
                                    title={t('pnn.portability-section.title')}
                                    message={t('pnn.portability-section.no-records')}
                                  />
                                )}
                              </>
                            ) : (
                              <div className="relative">
                                <div className="text-center p-4">
                                  <img
                                    alt="imageCh"
                                    className="mb-3 animated-Alert"
                                    src="/assets/media/illustrations/undraw/empty-data.svg"
                                    title="empty-table"
                                    width="40%"
                                  />
                                  <p className="mb-0 fs-3">{t('pnn.empty')}</p>
                                </div>
                              </div>
                            )
                          ) : (
                            <Loader />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}

export default PNNPortabilidadPage;
